import Layout from '@/components/Layout';
import { SEO } from '@/components/SEO';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { useEffect, useState, useRef } from 'react';
import { AiOutlineLoading3Quarters, AiOutlineReload } from "react-icons/ai";
import { useParams, useNavigate } from 'react-router-dom';
import { Separator } from "@/components/ui/separator";
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from '@/components/AiCommandPalette';
import { MdOutlineMoreVert } from 'react-icons/md';
import { TbFileText } from 'react-icons/tb';
import { Zap, Archive, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FiPackage } from 'react-icons/fi';
import { getReleaseStatusColor } from "@/lib/colorsMap";
import { ReleaseDropdown } from "@/components/ReleaseDropdown";
// Task Details Components
import TaskActivityTimeline from "@/components/TaskDetails/TaskActivityTimeline";
import TaskAttachments from "@/components/TaskDetails/TaskAttachments";
import TaskBreadcrumbRow from "@/components/TaskDetails/TaskBreadcrumbRow";
import TaskCommentEditor from "@/components/TaskDetails/TaskCommentEditor";
import TaskDescription from "@/components/TaskDetails/TaskDescription";
import TaskSidebar from "@/components/TaskDetails/TaskSidebar";
import TaskSummary from "@/components/TaskDetails/TaskSummary";
import TaskNotificationSubscription from "@/components/TaskDetails/TaskNotificationSubscription";
import { CompleteTaskDialog } from "@/components/CompleteTaskDialog";
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { TbAlertTriangle } from "react-icons/tb";

const DeleteConfirmationModal = ({ isOpen, onClose, onDelete, deleteLoading }) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <TbAlertTriangle className="h-5 w-5 text-red-500" />
                        <span>Permanently Delete Task</span>
                    </DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    <p className="text-primary">Are you sure you want to delete this task?</p>
                    <p className="mt-3 text-xs text-muted-foreground italic">This action cannot be undone and should be used with caution.</p>
                </DialogDescription>
            <DialogFooter>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button variant="destructive" onClick={onDelete} disabled={deleteLoading}>
                    {deleteLoading ? (
                        "Deleting..."
                    ) : (
                        "Delete"
                    )}
                </Button>
            </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const TaskDetails = () => {
    const params = useParams();
    const { organizationSlug } = params;
    // Handle both taskKey and projectKey parameter names
    // maybe we should just call them key?
    const taskKey = params.taskKey || params.projectKey;
    const { toast } = useToast();
    const [task, setTask] = useState(null);
    const [loadingStates, setLoadingStates] = useState({
        core: true,
        description: true,
        activities: true,
        sidebar: true,
        releases: true
    });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [closeComment, setCloseComment] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [isReopening, setIsReopening] = useState(false);
    const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
    const { activeOrganization, axiosInstance, user } = useAuth();
    const { showLoading, hideLoading, showFailed } = useGlobalLoading();
    const descriptionEditorRef = useRef(null);
    const commentEditorRef = useRef(null);
    const pendingDataRef = useRef({ content: null, activities: null });
    const { setCurrentAiPageContext } = useAiPageContext();
    const navigate = useNavigate();

    const reloadTaskData = (resetTask = true) => {
        // Reset loading states and pending data when task changes
        setLoadingStates({
            core: true,
            description: true,
            activities: true,
            sidebar: true,
            releases: true
        });
        if (resetTask) {
            setTask(null);
        }
        pendingDataRef.current = { content: null, activities: null, releases: null };

        // Fire all 4 requests in parallel for maximum speed
        const fetchCore = axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}`);
        const fetchContent = axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}/content`);
        const fetchActivities = axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}/activities`);
        const fetchReleases = axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}/releases`);

        // Core data loads first - renders UI immediately
        fetchCore
            .then(response => {
                const taskData = response.data;
                
                // Merge any pending data that arrived before core
                let mergedTask = { ...taskData };
                if (pendingDataRef.current.content) {
                    mergedTask = { ...mergedTask, ...pendingDataRef.current.content };
                }
                if (pendingDataRef.current.activities) {
                    mergedTask = { ...mergedTask, activities: pendingDataRef.current.activities };
                }
                if (pendingDataRef.current.releases) {
                    mergedTask = { ...mergedTask, ...pendingDataRef.current.releases };
                }
                
                setTask(mergedTask);
                // Set description as not loading - we have the structure, just not signed URLs
                // This allows S3Image skeletons to show while waiting for signed URLs
                setLoadingStates(prev => ({ ...prev, core: false, sidebar: false, description: false }));

                // Update editor with merged description if available
                if (descriptionEditorRef?.current && mergedTask.description) {
                    descriptionEditorRef.current.commands.setContent(mergedTask.description);
                }

                // Set page context for AI Command Palette
                setCurrentAiPageContext(
                    contextHelpers.createTaskContext(taskData)
                );
            })
            .catch(error => {
                console.error('Error fetching task core:', error);
                toast({
                    description: "Failed to fetch task details",
                    variant: "destructive"
                });
                setLoadingStates(prev => ({ ...prev, core: false, sidebar: false }));
            });

        // Content loads in parallel - updates description + attachments
        fetchContent
            .then(response => {
                const contentData = response.data;
                // Store in ref and try to merge into state
                pendingDataRef.current.content = contentData;
                
                setTask(prev => {
                    if (!prev) return prev; // Core hasn't loaded yet, it will merge from ref
                    const updated = { ...prev, ...contentData };
                    if (descriptionEditorRef?.current && contentData.description) {
                        descriptionEditorRef.current.commands.setContent(contentData.description);
                    }
                    return updated;
                });
                // Note: description loading state is set to false when core loads
                // so S3Image skeletons can show while waiting for signed URLs
            })
            .catch(error => {
                console.error('Error fetching task content:', error);
            });

        // Activities load in parallel - updates activity timeline
        fetchActivities
            .then(response => {
                const activitiesData = response.data;
                // Store in ref and try to merge into state
                pendingDataRef.current.activities = activitiesData.activities;
                
                setTask(prev => {
                    if (!prev) return prev; // Core hasn't loaded yet, it will merge from ref
                    return { ...prev, activities: activitiesData.activities };
                });
                setLoadingStates(prev => ({ ...prev, activities: false }));
            })
            .catch(error => {
                console.error('Error fetching task activities:', error);
                setLoadingStates(prev => ({ ...prev, activities: false }));
            });

        // Releases load in parallel - updates availableReleases and currentWeek
        fetchReleases
            .then(response => {
                const releasesData = response.data;
                // Store in ref and try to merge into state
                pendingDataRef.current.releases = releasesData;
                
                setTask(prev => {
                    if (!prev) return prev; // Core hasn't loaded yet, it will merge from ref
                    return { ...prev, ...releasesData };
                });
                setLoadingStates(prev => ({ ...prev, releases: false }));
            })
            .catch(error => {
                console.error('Error fetching task releases:', error);
                setLoadingStates(prev => ({ ...prev, releases: false }));
            });
    };

    useEffect(() => {
        reloadTaskData();
    }, [taskKey, organizationSlug]);

    // Helper to refresh activities after optimistic updates
    const refreshActivities = async () => {
        try {
            const res = await axiosInstance.get(`/tasks/${organizationSlug}/${taskKey}/activities`);
            setTask(prev => prev ? { ...prev, activities: res.data.activities } : prev);
        } catch (error) {
            console.error('Failed to refresh activities:', error);
        }
    };

    const handleTaskChange = (updatedTask) => {
        setTask(updatedTask);

        // Only update context when there are significant changes to the task
        // that would affect the context (e.g., summary change)
        if (updatedTask && (!task || updatedTask.summary !== task.summary)) {
            setCurrentAiPageContext(
                contextHelpers.createTaskContext(updatedTask)
            );
        }
    };

    const handleDeleteTask = async () => {
        try {
            setDeleteLoading(true);
            const projectKey = task.projectKey;
            const response = await axiosInstance.delete(`/tasks/${organizationSlug}/${taskKey}`);
            if (response.status === 200) {
                toast({
                    description: "Task deleted successfully",
                });
                navigate(`/${organizationSlug}/plan/${projectKey}`);
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            toast({
                description: "Failed to delete task",
                variant: "destructive"
            });
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleOpenDeleteConfirmationModal = () => {
        setIsDeleteConfirmationModalOpen(true);
    };

    const handleScopeChange = async (newScope, newRelease) => {
        try {
            // Optimistically update the UI
            const optimisticTask = {
                ...task,
                scope: newScope,
                release: newRelease,
                // Preserve the currentWeek data for immediate display
                currentWeek: task.currentWeek
            };
            handleTaskChange(optimisticTask);

            if (task.key) {
                // If we're removing from a release, first remove the release
                if (task.release && !newRelease) {
                    await axiosInstance.delete(`/tasks/${organizationSlug}/${task.key}/release`);
                }
                
                // Then update the scope
                const response = await axiosInstance.patch(`/tasks/${organizationSlug}/${task.key}`, {
                    scope: newScope
                });

                // Merge server response with preserved data
                const updatedTask = {
                    ...response.data,
                    currentWeek: task.currentWeek,
                    availableReleases: task.availableReleases
                };
                
                // Update with merged data
                handleTaskChange(updatedTask);
            }

            toast({
                description: newScope === 'tracked' ? "Moved to Active Week" : "Moved to Backlog",
            });

            setScopeDropdownOpen(false);
        } catch (error) {
            console.error('Error updating scope:', error);
            toast({
                variant: "destructive",
                description: "Failed to update scope",
            });
        }
    };

    const handleReleaseChange = async (release) => {
        try {
            // Optimistically update the UI
            const optimisticTask = {
                ...task,
                release: release
            };
            handleTaskChange(optimisticTask);

            if (task.key) {
                // Make the API call only if task has been created
                const response = await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/release`, {
                    release: { _id: release.id },
                    isNewRelease: false
                });

                // Update with server response
                handleTaskChange(response.data);
            }

            toast({
                description: "Release updated successfully",
            });

            setScopeDropdownOpen(false);
        } catch (error) {
            console.error('Error updating release:', error);
            toast({
                variant: "destructive",
                description: "Failed to update release",
            });
        }
    };

    const handleCloseTask = async () => {
        if (!closeComment.trim()) return;
        
        setIsClosing(true);
        showLoading();
        try {
            await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/close`, {
                comment: closeComment
            });
            
            // Update task in UI
            handleTaskChange({ ...task, closedAt: new Date() });
            
            // Refresh activities to show the close activity
            await refreshActivities();
            
            setShowCloseDialog(false);
            setCloseComment('');
            hideLoading();
        } catch (error) {
            console.error('Error closing task:', error);
            showFailed();
        } finally {
            setIsClosing(false);
        }
    };
    
    const handleReopenTask = async () => {
        setIsReopening(true);
        showLoading();
        try {
            await axiosInstance.post(`/tasks/${organizationSlug}/${task.key}/reopen`);
            
            // Update task in UI
            handleTaskChange({ ...task, closedAt: null });
            
            // Refresh activities to show the reopen activity
            await refreshActivities();
            
            hideLoading();
        } catch (error) {
            console.error('Error reopening task:', error);
            showFailed();
        } finally {
            setIsReopening(false);
        }
    };

    const handleCompleteTask = async ({ completedAt, targetStatus }) => {
        // Close dialog immediately - global loading will show progress
        setShowCompleteDialog(false);
        showLoading();
        
        try {
            const updateData = { completedAt: completedAt.toISOString() };
            
            // If we need to change status too
            if (targetStatus) {
                updateData.status = targetStatus;
            }
            
            const { data: updatedTask } = await axiosInstance.patch(
                `/tasks/${organizationSlug}/${task.key}`, 
                updateData
            );
            
            handleTaskChange(updatedTask);
            await refreshActivities();
            hideLoading();
            
            toast({
                description: "Task marked as complete",
            });
        } catch (error) {
            console.error('Error completing task:', error);
            showFailed();
            toast({
                variant: "destructive",
                description: "Failed to complete task",
            });
        }
    };

    // Separate releases into active and inactive (backlog)
    const activeReleases = [];
    const backlogReleases = [];
    
    if (task?.availableReleases) {
        task.availableReleases.forEach(release => {
            // A release is active if it has a start date in the past or today
            const isActive = release.startDate && new Date(release.startDate) <= new Date();
            if (isActive) {
                activeReleases.push(release);
            } else {
                backlogReleases.push(release);
            }
        });
    }

    if (!task && !loadingStates.core) {
        return <Layout>Task not found</Layout>;
    }

    return (
        <>
            <SEO
                title={task ? `${task.summary} (${taskKey})` : 'Task Details'}
                description={task ? `View details and updates for task ${taskKey}` : 'Loading task details...'}
            />
            <Layout>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 overflow-hidden">
                        <TaskBreadcrumbRow isLoading={loadingStates.core} organizationSlug={organizationSlug} task={task} />
                    </div>
                    {/* On mobile: wraps below breadcrumbs, release left-aligned, actions right-aligned */}
                    <div className="flex items-center justify-between gap-2 w-full md:w-auto">
                        <ReleaseDropdown
                            task={task}
                            onTaskChange={setTask}
                            onRefreshActivities={refreshActivities}
                            variant="minimal"
                            showReleaseDate={true}
                            isLoading={!task || loadingStates.releases}
                        />
                        <div className="flex items-center gap-2">
                            <TaskNotificationSubscription
                                task={task}
                                onTaskChange={handleTaskChange}
                                onRefreshActivities={refreshActivities}
                                organizationSlug={organizationSlug}
                                user={user}
                                userRole={task?.userRole}
                                isLoading={loadingStates.core}
                            />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 bg-muted/60 hover:bg-muted/80 shadow-sm border border-muted/30"
                                    >
                                        <MdOutlineMoreVert className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {!task?.closedAt && (
                                        <DropdownMenuItem onClick={() => setShowCompleteDialog(true)}>
                                            {task?.completedAt ? 'Edit Completed Date' : 'Mark as Complete'}
                                        </DropdownMenuItem>
                                    )}
                                    {task?.closedAt ? (
                                        <DropdownMenuItem
                                            onClick={handleReopenTask}
                                            disabled={isReopening}
                                        >
                                            {isReopening ? 'Reopening...' : 'Reopen Task'}
                                        </DropdownMenuItem>
                                    ) : (
                                        <DropdownMenuItem onClick={() => setShowCloseDialog(true)}>
                                            Close Task
                                        </DropdownMenuItem>
                                    )}
                                    {activeOrganization?.role?.toLowerCase() === 'admin' && (
                                        <>
                                            <DropdownMenuItem onClick={() => reloadTaskData(false)}>
                                                Refresh
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                className="text-red-600 focus:text-red-600"
                                                onClick={handleOpenDeleteConfirmationModal}
                                            >
                                                <TbAlertTriangle className="mr-2 h-4 w-4" />
                                                Delete Task
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-4 md:gap-6 mt-5">
                    <div className="col-span-4 md:col-span-3 space-y-6 pb-6">
                        <Card className="overflow-hidden">
                            <CardContent className="pt-8 pb-10 px-4 md:px-6">
                                <div className="max-w-3xl mx-auto w-full min-w-0">
                                    <TaskSummary
                                        isLoading={loadingStates.core}
                                        task={task}
                                        onTaskChange={handleTaskChange}
                                    />



                                    <TaskDescription
                                        isLoading={loadingStates.description}
                                        task={task}
                                        onTaskChange={handleTaskChange}
                                        editorRef={descriptionEditorRef}
                                    />

                                    <div className="mt-3">
                                        <TaskAttachments
                                            task={task}
                                            onTaskChange={handleTaskChange}
                                            descriptionEditorRef={descriptionEditorRef}
                                        />
                                    </div>
                                    <Separator className="mt-6" />
                                    <div className="md:hidden">
                                        <TaskSidebar
                                            isLoading={loadingStates.sidebar}
                                            task={task}
                                            onTaskChange={handleTaskChange}
                                            onRefreshActivities={refreshActivities}
                                            organizationSlug={organizationSlug}
                                        />
                                    </div>
                                    <div className="mt-6">
                                        <TaskActivityTimeline
                                            isLoading={loadingStates.activities}
                                            activities={task?.activities}
                                            availableStatuses={task?.availableStatuses}
                                            task={task}
                                            onTaskChange={handleTaskChange}
                                        />
                                        <div className="mt-5">
                                            <TaskCommentEditor
                                                isLoading={loadingStates.core}
                                                task={task}
                                                onTaskChange={handleTaskChange}
                                                editorRef={commentEditorRef}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="hidden md:block">
                        <TaskSidebar
                            isLoading={loadingStates.sidebar}
                            task={task}
                            onTaskChange={handleTaskChange}
                            onRefreshActivities={refreshActivities}
                            organizationSlug={organizationSlug}
                        />
                    </div>
                </div>
            </Layout>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                onClose={() => setIsDeleteConfirmationModalOpen(false)}
                onDelete={handleDeleteTask}
                deleteLoading={deleteLoading}
            />
            
            {/* Close Dialog */}
            <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Close {task?.key}?</DialogTitle>
                    </DialogHeader>
                    
                    <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-3 space-y-2">
                        <p className="text-xs font-medium text-violet-600 dark:text-violet-400">What happens when you close a task:</p>
                        <ul className="text-xs text-violet-700 dark:text-violet-200 space-y-1">
                            <li className="flex items-start gap-2">
                                <span className="text-violet-500">•</span>
                                <span>Task becomes read-only and can no longer be edited</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-violet-500">•</span>
                                <span>Removed from Active Week and Backlog views</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-violet-500">•</span>
                                <span>You can reopen the task later if needed</span>
                            </li>
                        </ul>
                    </div>
                    
                    <div className="space-y-2">
                        <Textarea 
                            id="close-comment"
                            placeholder="Add a comment explaining why this task is being closed..."
                            value={closeComment}
                            onChange={(e) => setCloseComment(e.target.value)}
                            rows={3}
                            className="resize-none text-xs"
                        />
                    </div>
                    
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowCloseDialog(false);
                                setCloseComment('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCloseTask}
                            disabled={closeComment.trim() === ''}
                            loading={isClosing}
                        >
                            Close Task
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Complete Task Dialog */}
            <CompleteTaskDialog
                open={showCompleteDialog}
                onOpenChange={setShowCompleteDialog}
                task={task}
                onComplete={handleCompleteTask}
            />
        </>
    );
};

export default TaskDetails;